/**
 * Local visual tokens for the bounty feature (creator side). Chakra v2 style props
 * consume these directly — nothing here touches the global theme.
 * Values follow the creator revamp `ff` palette (src/theme.ts).
 */
import { ff, navCtaOffset } from '../../theme';

export const bountyColors = {
  ink: ff.ink,
  body: '#3E3E42',
  muted: ff.text2,
  paper: '#FFFFFF',
  mist: ff.grey,
  line: ff.line,
  green: ff.ink,
  greenTint: '#E4F3CC',
  greenDeep: '#2C5208',
  purple: ff.ink,
  purpleTint: '#FFFFFF',
  amber: '#8A4B00',
  amberTint: '#FFF1D6',
  blue: ff.lime,
  blueTint: ff.ink,
  /** Board "needs action" red (rejected intro / proposal notes). */
  red: '#A1260F',
  redTint: '#FBE1DC',
  redInk: '#6E1A0A',
};

/**
 * Creator v2 tokens, now pointed at the revamp `ff` palette (lime CTA on ink text).
 * Export names are kept so existing importers don't change.
 */
export const bountyV2Colors = {
  ink: ff.ink,
  body: '#3E3E42',
  muted: ff.text2,
  faint: ff.muted,
  line: ff.line,
  hair: ff.line,
  cardLine: '#E4E4E0',
  green: ff.ink,
  greenVivid: ff.lime,
  /** One CTA colour for every solid primary action: lime with ink text. */
  cta: ff.lime,
  ctaHover: '#B8E62A',
  tint: ff.limeSurface,
  tint2: ff.grey,
  tintLine: ff.line,
  tintText: ff.ink,
  deepGreenText: ff.ink,
  segActiveBg: ff.ink,
  segActiveText: '#FFFFFF',
  segDivider: ff.line,
  chipBorder: ff.border,
  chipActiveBorder: ff.ink,
  chipActiveText: ff.ink,
  fieldBorder: ff.border,
  fieldIcon: ff.ink,
  placeholder: ff.muted,
  helper: ff.text2,
  required: ff.danger,
  errorText: '#A1260F',
  errorBorder: '#F3B6B8',
  errorBg: '#FBE1DC',
};

/** Page background shared by the v2 screens — pass to `bgGradient`, with `bgColor` behind it. */
export const bountyV2ScreenBg = 'linear(180deg, #FFFFFF 0%, #FFFFFF 100%)';
export const bountyV2ScreenBgColor = '#FFFFFF';

export const bountyV2Shadows = {
  card: 'none',
  chip: 'none',
  search: 'none',
  claimBtn: 'none',
  promoBtn: 'none',
  cta: 'none',
  floating: 'none',
  back: 'none',
};

/** Focus ring shared by the v2 search field and form inputs. */
export const bountyV2FocusRing = {
  borderColor: ff.ink,
  boxShadow: `0 0 0 1px ${ff.ink}`,
};

export const eyebrowStyle = {
  fontFamily: 'display',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: ff.text2,
};

export const tabularNums = { fontVariantNumeric: 'tabular-nums' } as const;

/** Shared style props for form inputs (Input/Select/Textarea): board 48px, radius 14, 0 14px padding, 16px font. */
export const formInputStyle = {
  bg: '#FFFFFF',
  borderColor: ff.border,
  borderRadius: '14px',
  h: '48px',
  px: '14px',
  fontSize: '16px',
  color: ff.ink,
  _placeholder: { color: ff.muted },
  _hover: { borderColor: ff.border },
  _focusVisible: { borderColor: ff.ink, boxShadow: `0 0 0 1px ${ff.ink}` },
  _invalid: { borderColor: '#A1260F', boxShadow: '0 0 0 1px #A1260F' },
};

/** Shared style props for form labels: 14px/700/ink. */
export const formLabelStyle = {
  fontSize: '14px',
  fontWeight: 700,
  color: ff.ink,
  mb: '6px',
};

/** Page scaffold bottom padding. MainLayout already reserves the floating nav's space on /bounties. */
export const pageBottomPadding = '24px';
/** Used instead of pageBottomPadding on pages with a sticky bottom CTA (clears the ~84px bar). */
export const pageBottomPaddingWithStickyCta = '104px';
/** Fixed CTAs park just above the floating tab bar, which MainLayout renders on every /bounties route. */
export const stickyCtaBottomOffset = navCtaOffset;

export type PillTone = 'green' | 'amber' | 'purple' | 'blue' | 'gray';

/** Tinted pill tones shared by chips, badges, and status labels across the feature. */
export const pillTones: Record<PillTone, { bg: string; color: string; boxShadow?: string }> = {
  green: { bg: bountyColors.greenTint, color: bountyColors.greenDeep },
  /** "Needs new contact" / not-approved style: board red. */
  amber: { bg: bountyColors.redTint, color: bountyColors.red },
  purple: { bg: '#FFFFFF', color: ff.ink, boxShadow: `inset 0 0 0 1.5px ${ff.ink}` },
  /** "Intro submitted": ink pill with lime text. */
  blue: { bg: ff.ink, color: ff.lime },
  gray: { bg: '#F0F0EE', color: '#3E3E42' },
};

/** Maps the colorScheme strings returned by utils.ts chip helpers to a pill tone. */
export function colorSchemeToTone(colorScheme: string): PillTone {
  switch (colorScheme) {
    case 'green': return 'green';
    case 'blue': return 'blue';
    case 'orange': return 'amber';
    case 'purple': return 'purple';
    default: return 'gray';
  }
}
